import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ProtectedRoute } from "./ProtectedRoute";
import { GuestRoute } from "./GuestRoute";
import { AdminProtectedRoute } from "./AdminProtectedRoute";
import { FacilitatorProtectedRoute } from "./FacilitatorProtectedRoute";
import { EcosystemHeadProtectedRoute } from "./EcosystemHeadProtectedRoute";
import { RouteFallback } from "./RouteFallback";

const AppLayout = lazy(() =>
  import("@/components/layout/AppLayout").then((m) => ({ default: m.AppLayout }))
);
const LandingLayout = lazy(() => import("@/components/layout/LandingPageLayout"));
const AdminLayout = lazy(() =>
  import("@/components/layout/AdminLayout").then((m) => ({ default: m.AdminLayout }))
);
const FacilitatorLayout = lazy(() =>
  import("@/components/layout/FacilitatorLayout").then((m) => ({ default: m.FacilitatorLayout }))
);
const EcosystemHeadLayout = lazy(() =>
  import("@/components/layout/EcosystemHeadLayout").then((m) => ({ default: m.EcosystemHeadLayout }))
);

const DiscoverPage = lazy(() => import("@/pages/discover/DiscoverPage"));
const SectorEcosystemPage = lazy(() => import("@/pages/sector-ecosystem/SectorEcosystemPage"));
const SectorOpportunitiesListPage = lazy(() =>
  import("@/pages/sector-ecosystem/SectorCompaniesListPage").then((m) => ({
    default: m.SectorOpportunitiesListPage,
  })),
);
const SectorCompaniesBrowsePage = lazy(() =>
  import("@/pages/sector-ecosystem/SectorCompaniesListPage").then((m) => ({
    default: m.SectorCompaniesBrowsePage,
  })),
);
const SectorCollaborationModelsListPage = lazy(() =>
  import("@/pages/sector-ecosystem/SectorCompaniesListPage").then((m) => ({
    default: m.SectorCollaborationModelsListPage,
  })),
);
const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const LoginTwoFactorPage = lazy(() => import("@/two-factor/pages/LoginTwoFactorPage"));
const SignupPage = lazy(() => import("@/pages/signup/SignupPage"));
const SignupDocumentsPage = lazy(() => import("@/pages/signup/SignupDocumentsPage"));
const RegistrationSuccessPage = lazy(() => import("@/pages/signup/RegistrationSuccessPage"));
const ForgotPassword = lazy(() => import("@/pages/forgot-password/ForgotPassword"));
const ResendEmail = lazy(() => import("@/pages/resend-email/ResendEmail"));
const ResetPassword = lazy(() => import("@/pages/reset-password/ResetPassword"));
const ActivateAccountPage = lazy(() => import("@/pages/activate-account/ActivateAccountPage"));
const ReviewAccountPage = lazy(() => import("@/pages/review-account/ReviewAccountPage"));

const Maintenance = lazy(() => import("@/pages/maintenance/Maintenance"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

const DiscoverCompanyProfileGate = lazy(() =>
  import("@/routes/DiscoverCompanyProfileGate").then((m) => ({ default: m.DiscoverCompanyProfileGate }))
);
const AnonymousProfile = lazy(() => import("@/components/shared/AnonymousCompanyProfile/AnonymousProfile"));
const LoggedInCompanyWorkspaceRedirect = lazy(() => import("@/routes/LoggedInCompanyWorkspaceRedirect"));

const DashboardPage = lazy(() => import("@/pages/dashboard/DashboardPage"));
const CreateProfilePage = lazy(() => import("@/pages/create-profile/CreateProfilePage"));
const MyProfilePage = lazy(() => import("@/pages/profile/MyProfilePage"));
const EditProfile = lazy(() => import("@/pages/edit-profile/EditProfile"));
const SettingsPage = lazy(() => import("@/pages/mysupport/SettingsPage"));
const UserAccountProfilePage = lazy(() => import("@/pages/account/UserAccountProfilePage"));
const EnableTwoFactorPage = lazy(() => import("@/two-factor/pages/EnableTwoFactorPage"));
const InterestsPage = lazy(() => import("@/pages/interests/InterestsPage"));
const MatchSuccessPage = lazy(() => import("@/pages/interests/components/ItsaMatchPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/interests/components/PaymentSuccessPage"));
const CollaborationsPage = lazy(() => import("@/pages/collaborations/CollaborationsPage"));
const CollaborationWorkspacePage = lazy(() => import("@/pages/collaborations/CollaborationWorkspacePage"));
const CollaborationCompanyWorkspacePage = lazy(
  () => import("@/pages/collaborations/CollaborationCompanyWorkspacePage")
);
const MatchedProfileView = lazy(() => import("@/pages/profile/MatchedView"));
const ShortlistPage = lazy(() => import("@/pages/shortlist/ShortlistPage"));
const CreditsPage = lazy(() => import("@/pages/credits/CreditsPage"));
const BuyCreditsPage = lazy(() => import("@/pages/credits/components/BuyCreditsPage"));
const InvoicesPage = lazy(() => import("@/pages/invoices/InvoicesPage"));
const WalletPage = lazy(() => import("@/pages/invoices/WalletPage"));

const FacilitatorLoginPage = lazy(() => import("@/pages/facilitator/FacilitatorLoginPage"));
const FacilitatorDashboardPage = lazy(() => import("@/pages/facilitator/FacilitatorDashboardPage"));
const FacilitatorCompanyDetailPage = lazy(() => import("@/pages/facilitator/FacilitatorCompanyDetailPage"));
const FacilitatorCompanyAnonymousProfilePage = lazy(
  () => import("@/pages/facilitator/FacilitatorCompanyAnonymousProfilePage"),
);
const FacilitatorManagedCompanyProfilePage = lazy(
  () => import("@/pages/facilitator/FacilitatorManagedCompanyProfilePage"),
);
const FacilitatorAddCompanyPage = lazy(() => import("@/pages/facilitator/FacilitatorAddCompanyPage"));
const FacilitatorPartnerRecommendationsPage = lazy(
  () => import("@/pages/facilitator/FacilitatorPartnerRecommendationsPage"),
);

const EcosystemHeadDashboardPage = lazy(() => import("@/pages/ecosystem-head/EcosystemHeadDashboardPage"));
const EcosystemHeadRequestsPage = lazy(() => import("@/pages/ecosystem-head/EcosystemHeadRequestsPage"));
const EcosystemHeadQuestionnairePage = lazy(() => import("@/pages/ecosystem-head/EcosystemHeadQuestionnairePage"));
const EcosystemHeadVerificationPage = lazy(
  () => import("@/pages/ecosystem-head/EcosystemHeadVerificationPage"),
);
const EcosystemHeadAppointmentsPage = lazy(
  () => import("@/pages/ecosystem-head/EcosystemHeadAppointmentsPage"),
);
const EcosystemHeadPartnerRecommendationsPage = lazy(
  () => import("@/pages/ecosystem-head/EcosystemHeadPartnerRecommendationsPage"),
);
const EcosystemHeadCompanyAnswersPage = lazy(
  () => import("@/pages/ecosystem-head/EcosystemHeadCompanyAnswersPage"),
);
const EcosystemHeadManagedCompanyProfilePage = lazy(
  () => import("@/pages/ecosystem-head/EcosystemHeadManagedCompanyProfilePage"),
);
const CompanyEcosystemHeadWorkspacePage = lazy(
  () => import("@/pages/ecosystem-heads/CompanyEcosystemHeadWorkspacePage"),
);
const CompanyEcosystemHeadMoreInfoPage = lazy(
  () => import("@/pages/ecosystem-heads/CompanyEcosystemHeadMoreInfoPage"),
);
const CompanyEcosystemHeadAppointmentsPage = lazy(
  () => import("@/pages/ecosystem-heads/CompanyEcosystemHeadAppointmentsPage"),
);
const EcosystemHeadCompanyAnonymousProfilePage = lazy(
  () => import("@/pages/ecosystem-head/EcosystemHeadCompanyAnonymousProfilePage"),
);
const EcosystemHeadsExplorePage = lazy(() => import("@/pages/ecosystem-heads/EcosystemHeadsExplorePage"));
const EcosystemHeadsIndustryPage = lazy(() => import("@/pages/ecosystem-heads/EcosystemHeadsIndustryPage"));
const ClientEcosystemHeadProfilePage = lazy(
  () => import("@/pages/ecosystem-heads/ClientEcosystemHeadProfilePage"),
);
const MyEcosystemHeadsPage = lazy(() => import("@/pages/ecosystem-heads/MyEcosystemHeadsPage"));

const AdminLoginPage = lazy(() => import("@/pages/admin/AdminLoginPage"));
const AdminUsersPage = lazy(() => import("@/pages/admin/users/AdminUsersPage"));
const AdminFacilitatorsPage = lazy(() => import("@/pages/admin/users/AdminFacilitatorsPage"));
const AdminFacilitatorDetailPage = lazy(() => import("@/pages/admin/users/AdminFacilitatorDetailPage"));
const AdminEcosystemHeadsPage = lazy(() => import("@/pages/admin/users/AdminEcosystemHeadsPage"));
const AdminEcosystemHeadDetailPage = lazy(() => import("@/pages/admin/users/AdminEcosystemHeadDetailPage"));
const SubAdminManagementPage = lazy(() => import("@/pages/admin/users/SubAdminManagementPage"));
const AdminOuterUsersPage = lazy(() => import("@/pages/admin/outer-users/AdminOuterUsersPage"));
const AdminCreditAdjustmentHistoryPage = lazy(
  () => import("@/pages/admin/users/AdminCreditAdjustmentHistoryPage")
);
const AdminBulkMailHistoryPage = lazy(() => import("@/pages/admin/bulk-mail/AdminBulkMailHistoryPage"));
const AdminBulkMailPage = lazy(() => import("@/pages/admin/bulk-mail/AdminBulkMailPage"));
const AdminCompanyOnboardingChartPage = lazy(
  () => import("@/pages/admin/company-lists/AdminCompanyOnboardingChartPage")
);
const AdminCountriesMapPage = lazy(() => import("@/pages/admin/company-lists/AdminCountriesMapPage"));
const AdminIndustriesMapPage = lazy(() => import("@/pages/admin/company-lists/AdminIndustriesMapPage"));
const AdminDashboardPage = lazy(() => import("@/pages/admin/company-lists/AdminDashboardPage"));
const AdminPendingVerificationPage = lazy(
  () => import("@/pages/admin/company-lists/AdminPendingVerificationPage")
);
const AdminIndirectVerificationPage = lazy(
  () => import("@/pages/admin/indirect-verification/AdminIndirectVerificationPage")
);
const AdminMeilisearchReindexPage = lazy(() => import("@/pages/admin/meilisearch/AdminMeilisearchReindexPage"));
const AdminCollaborationTable = lazy(() => import("@/pages/admin/collaboration-lists/AdminCollaborationTable"));
const AdminInterestManagement = lazy(() => import("@/pages/admin/interest-management/InterestManagement"));
const AdminPartnerMatchPage = lazy(() => import("@/pages/admin/partner-match/AdminPartnerMatchPage"));
const AdminExpressInterestPreviewPage = lazy(
  () => import("@/pages/admin/express-interest/AdminExpressInterestPreviewPage")
);
const AdminDiscoverTable = lazy(() => import("@/pages/admin/discover-listings/AdminDiscoverTable"));
const AdminGuidancePage = lazy(() => import("@/pages/admin/guidance-table/AdminGuidancePage"));
const AdminCompanyDetailView = lazy(() => import("@/pages/admin/company-lists/components/AdminMatchedView"));
const AdminCompanyFullEditPage = lazy(() => import("@/pages/admin/company-lists/AdminCompanyFullEditPage"));
const AdminOnboardingListPage = lazy(() => import("@/pages/admin/onboarding/AdminOnboardingListPage"));
const AdminOnboardingCreatePage = lazy(() => import("@/pages/admin/onboarding/AdminOnboardingCreatePage"));
const AdminOnboardingEditPage = lazy(() =>
  import("@/pages/admin/onboarding/AdminOnboardingCreatePage").then((m) => ({
    default: m.AdminOnboardingEditPage,
  }))
);
const AdminEcosystemNewsListPage = lazy(
  () => import("@/pages/admin/ecosystem-news/AdminEcosystemNewsListPage"),
);
const AdminEcosystemNewsCreatePage = lazy(
  () => import("@/pages/admin/ecosystem-news/AdminEcosystemNewsCreatePage"),
);
const AdminEcosystemNewsEditPage = lazy(() =>
  import("@/pages/admin/ecosystem-news/AdminEcosystemNewsCreatePage").then((m) => ({
    default: m.AdminEcosystemNewsEditPage,
  })),
);
const AdminCollaborationDetailPage = lazy(
  () => import("@/pages/admin/collaboration-lists/components/WorkspaceView")
);
function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public routes */}
        <Route element={<GuestRoute />}>
          <Route element={<LandingLayout />}>
            <Route path="/" element={<DiscoverPage />} />
            <Route path="/discover" element={<DiscoverPage />} />
          </Route>
        </Route>
        {/* Admin/shared discover company URL — must work for guests and logged-in clients */}
        <Route path="/discover/:id" element={<DiscoverCompanyProfileGate />}>
          <Route element={<LandingLayout />}>
            <Route index element={<AnonymousProfile pageID="DISCOVER_PUBLIC" />} />
          </Route>
        </Route>
        <Route path="/registration-success" element={<RegistrationSuccessPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/2fa" element={<LoginTwoFactorPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/signup/company-details" element={<Navigate to="/login" replace />} />
        <Route path="/signup/documents" element={<SignupDocumentsPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/resend-email" element={<ResendEmail />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/activate-account" element={<ActivateAccountPage />} />
        <Route path="/review-account" element={<ReviewAccountPage />} />
        <Route path="/facilitator/login" element={<FacilitatorLoginPage />} />
        <Route path="/facilitator/signup" element={<FacilitatorLoginPage />} />

        {/* Protected routes with AppShell layout */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/create-profile" element={<CreateProfilePage />} />
          <Route path="/profile" element={<MyProfilePage />} />
          <Route path="/my-discovery" element={<DiscoverPage />} />
          <Route path="/my-discovery/:id" element={<LoggedInCompanyWorkspaceRedirect />} />
          <Route path="/sector/:slug" element={<SectorEcosystemPage />} />
          <Route path="/sector/:slug/opportunities" element={<SectorOpportunitiesListPage />} />
          <Route path="/sector/:slug/companies" element={<SectorCompaniesBrowsePage />} />
          <Route path="/sector/:slug/collaboration-models" element={<SectorCollaborationModelsListPage />} />
          <Route path="/requests" element={<InterestsPage />} />
          <Route path="/collaborations" element={<CollaborationsPage />} />
          <Route path="/shortlist" element={<ShortlistPage />} />
          <Route path="/shortlist/:id" element={<LoggedInCompanyWorkspaceRedirect />} />
          <Route path="/credits" element={<CreditsPage />} />
          <Route path="/credits/buy" element={<BuyCreditsPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/invoices/wallet" element={<WalletPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/account" element={<UserAccountProfilePage />} />
          <Route path="/ecosystem-heads" element={<EcosystemHeadsExplorePage />} />
          <Route path="/ecosystem-heads/my" element={<MyEcosystemHeadsPage />} />
          <Route path="/ecosystem-heads/my/:linkId/more-info" element={<CompanyEcosystemHeadMoreInfoPage />} />
          <Route path="/ecosystem-heads/my/:linkId/appointments" element={<CompanyEcosystemHeadAppointmentsPage />} />
          <Route path="/ecosystem-heads/my/:linkId" element={<CompanyEcosystemHeadWorkspacePage />} />
          <Route path="/ecosystem-heads/sector/:sectorSlug" element={<EcosystemHeadsIndustryPage />} />
          <Route path="/ecosystem-heads/:industry" element={<EcosystemHeadsIndustryPage />} />
          <Route path="/ecosystem-heads/:industry/:headUserId" element={<ClientEcosystemHeadProfilePage />} />
          <Route path="/two-factor/setup" element={<EnableTwoFactorPage />} />
          <Route path="/profile/edit" element={<EditProfile />} />
          <Route path="/requests/:id" element={<LoggedInCompanyWorkspaceRedirect />} />
          <Route path="/requests/match-success/:id" element={<MatchSuccessPage />} />
          <Route path="/requests/payment-success/:id" element={<PaymentSuccessPage />} />

          <Route path="/collaborations/company/:companyId" element={<CollaborationCompanyWorkspacePage />} />
          <Route path="/collaborations/:id" element={<CollaborationWorkspacePage />} />
          <Route path="/collaborations/:id/profile" element={<MatchedProfileView />} />
        </Route>

        {/* ============ Admin routes ============ */}
        <Route path="/admin/login" element={<AdminLoginPage />} />

        <Route
          element={
            <AdminProtectedRoute>
              <AdminLayout />
            </AdminProtectedRoute>
          }
        >
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/facilitators" element={<AdminFacilitatorsPage />} />
          <Route path="/admin/facilitators/:id" element={<AdminFacilitatorDetailPage />} />
          <Route path="/admin/ecosystem-heads" element={<AdminEcosystemHeadsPage />} />
          <Route path="/admin/ecosystem-heads/:id" element={<AdminEcosystemHeadDetailPage />} />
          <Route path="/admin/sub-admins" element={<SubAdminManagementPage />} />
          <Route path="/admin/outer-users" element={<AdminOuterUsersPage />} />
          <Route path="/admin/credit-history" element={<AdminCreditAdjustmentHistoryPage />} />
          <Route path="/admin/bulk-mail/history" element={<AdminBulkMailHistoryPage />} />
          <Route path="/admin/bulk-mail" element={<AdminBulkMailPage />} />
          <Route path="/admin/company-onboarding" element={<AdminCompanyOnboardingChartPage />} />
          <Route path="/admin/onboarding" element={<AdminOnboardingListPage />} />
          <Route path="/admin/onboarding/create" element={<AdminOnboardingCreatePage />} />
          <Route path="/admin/onboarding/:id/edit" element={<AdminOnboardingEditPage />} />
          <Route path="/admin/ecosystem-news" element={<AdminEcosystemNewsListPage />} />
          <Route path="/admin/ecosystem-news/create" element={<AdminEcosystemNewsCreatePage />} />
          <Route path="/admin/ecosystem-news/:id/edit" element={<AdminEcosystemNewsEditPage />} />
          <Route path="/admin/countries-map" element={<AdminCountriesMapPage />} />
          <Route path="/admin/industries-map" element={<AdminIndustriesMapPage />} />
          <Route path="/admin/companies" element={<AdminDashboardPage />} />
          <Route path="/admin/pending-verification" element={<AdminPendingVerificationPage />} />
          <Route path="/admin/indirect-verification" element={<AdminIndirectVerificationPage />} />
          <Route path="/admin/meilisearch" element={<AdminMeilisearchReindexPage />} />
          <Route path="/admin/collaborations" element={<AdminCollaborationTable />} />
          <Route path="/admin/interest" element={<AdminInterestManagement />} />
          <Route path="/admin/partner-match" element={<AdminPartnerMatchPage />} />
          <Route path="/admin/express-interest-preview" element={<AdminExpressInterestPreviewPage />} />
          <Route path="/admin/discover" element={<AdminDiscoverTable />} />
          <Route path="/admin/resource" element={<AdminGuidancePage />} />
          <Route path="/admin/companies/:id" element={<AdminCompanyDetailView />} />
          <Route path="/admin/companies/:id/edit" element={<AdminCompanyFullEditPage />} />
          <Route path="/admin/collaborations/:id" element={<AdminCollaborationDetailPage />} />
          <Route path="/admin/two-factor" element={<EnableTwoFactorPage />} />
        </Route>

        {/* ============ Ecosystem Head routes ============ */}
        <Route element={<EcosystemHeadProtectedRoute />}>
          <Route element={<EcosystemHeadLayout />}>
            <Route path="/ecosystem-head/my-discovery" element={<DiscoverPage />} />
            <Route path="/ecosystem-head/shortlist" element={<ShortlistPage />} />
            <Route path="/ecosystem-head/partner-suggestions" element={<EcosystemHeadPartnerRecommendationsPage />} />
            <Route path="/ecosystem-head/dashboard" element={<EcosystemHeadDashboardPage />} />
            <Route path="/ecosystem-head/requests" element={<EcosystemHeadRequestsPage />} />
            <Route path="/ecosystem-head/questionnaire" element={<EcosystemHeadQuestionnairePage />} />
            <Route path="/ecosystem-head/verification" element={<EcosystemHeadVerificationPage />} />
            <Route path="/ecosystem-head/appointments" element={<EcosystemHeadAppointmentsPage />} />
            <Route path="/ecosystem-head/companies/:linkId/answers" element={<EcosystemHeadCompanyAnswersPage />} />
            <Route
              path="/ecosystem-head/companies/:id/company-profile"
              element={<EcosystemHeadManagedCompanyProfilePage />}
            />
            <Route
              path="/ecosystem-head/companies/:id/profile"
              element={<EcosystemHeadCompanyAnonymousProfilePage />}
            />
            <Route path="/ecosystem-head/account" element={<UserAccountProfilePage />} />
            <Route path="/ecosystem-head/settings" element={<SettingsPage />} />
            <Route path="/ecosystem-head/two-factor/setup" element={<EnableTwoFactorPage />} />
          </Route>
        </Route>

        {/* ============ Facilitator routes ============ */}
        <Route element={<FacilitatorProtectedRoute />}>
          <Route element={<FacilitatorLayout />}>
            <Route path="/facilitator/dashboard" element={<FacilitatorDashboardPage />} />
            <Route path="/facilitator/add-company" element={<FacilitatorAddCompanyPage />} />
            <Route path="/facilitator/managed" element={<FacilitatorDashboardPage />} />
            <Route path="/facilitator/my-discovery" element={<DiscoverPage />} />
            <Route path="/facilitator/shortlist" element={<ShortlistPage />} />
            <Route path="/facilitator/partner-suggestions" element={<FacilitatorPartnerRecommendationsPage />} />
            <Route
              path="/facilitator/companies/:id/company-profile"
              element={<FacilitatorManagedCompanyProfilePage />}
            />
            <Route path="/facilitator/companies/:id/profile" element={<FacilitatorCompanyAnonymousProfilePage />} />
            <Route path="/facilitator/companies/:id" element={<FacilitatorCompanyDetailPage />} />
            <Route path="/facilitator/account" element={<UserAccountProfilePage />} />
            <Route path="/facilitator/settings" element={<SettingsPage />} />
            <Route path="/facilitator/two-factor/setup" element={<EnableTwoFactorPage />} />
          </Route>
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
        <Route path="/maintenance" element={<Maintenance />} />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
